import { useState, useCallback } from 'react';

/**
 * Custom hook for managing row selection in tables
 * Handles single selection, shift-select for range, and select-all
 * 
 * @param {Array} currentData - Current data array (companies or contacts)
 * @returns {Object} - Selection state and operations
 */
const useRowSelection = (currentData = []) => {
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);

  /**
   * Handle individual row selection
   * Supports shift-click for range selection
   * 
   * @param {string} rowId - Row ID to select/deselect
   * @param {number} index - Row index
   * @param {Event} event - Click event
   */
  const handleRowSelect = useCallback((rowId, index, event) => {
    if (event?.shiftKey && lastSelectedIndex !== null) {
      // Shift+click: select range
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      
      setSelectedRows(prev => {
        const newSet = new Set(prev);
        for (let i = start; i <= end; i++) {
          if (currentData[i]) {
            newSet.add(currentData[i].id);
          }
        }
        return newSet;
      });
    } else {
      // Regular click: toggle single row
      setSelectedRows(prev => {
        const newSet = new Set(prev);
        if (newSet.has(rowId)) {
          newSet.delete(rowId);
        } else {
          newSet.add(rowId);
        }
        return newSet;
      });
      setLastSelectedIndex(index);
    }
  }, [currentData, lastSelectedIndex]);

  /**
   * Select or deselect all rows
   * 
   * @param {boolean} checked - Whether to select all or deselect all
   */
  const handleSelectAll = useCallback((checked) => {
    if (checked) {
      setSelectedRows(new Set(currentData.map(item => item.id)));
    } else {
      setSelectedRows(new Set());
    }
  }, [currentData]);

  /**
   * Clear all selections
   */
  const clearSelection = useCallback(() => {
    setSelectedRows(new Set());
    setLastSelectedIndex(null);
  }, []);

  /**
   * Replace current selection with provided row ids.
   *
   * @param {Array<string>} rowIds - Row IDs to mark selected
   */
  const setSelectedRowsFromIds = useCallback((rowIds = []) => {
    setSelectedRows(new Set(rowIds));
    setLastSelectedIndex(null);
  }, []);

  return {
    selectedRows,
    lastSelectedIndex,
    handleRowSelect,
    handleSelectAll,
    clearSelection,
    setSelectedRowsFromIds,
  };
};

export default useRowSelection;

